// src/lib/firestore.js
import {
  collection, doc, getDocs, getDoc,
  addDoc, updateDoc, deleteDoc, setDoc,
  orderBy, query, serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase";

const col = (name) => collection(db, name);
const getAll = async (name, order = "order") => {
  try {
    const snap = await getDocs(query(col(name), orderBy(order, "asc")));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch {
    const snap = await getDocs(col(name));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
};

export const getProjects      = () => getAll("projects");
export const addProject       = (d) => addDoc(col("projects"), { ...d, createdAt: serverTimestamp() });
export const updateProject    = (id, d) => updateDoc(doc(db, "projects", id), d);
export const deleteProject    = (id) => deleteDoc(doc(db, "projects", id));

export const getExperiences   = () => getAll("experiences");
export const addExperience    = (d) => addDoc(col("experiences"), { ...d, createdAt: serverTimestamp() });
export const updateExperience = (id, d) => updateDoc(doc(db, "experiences", id), d);
export const deleteExperience = (id) => deleteDoc(doc(db, "experiences", id));

export const getSkills        = () => getAll("skills");
export const addSkill         = (d) => addDoc(col("skills"), { ...d, createdAt: serverTimestamp() });
export const updateSkill      = (id, d) => updateDoc(doc(db, "skills", id), d);
export const deleteSkill      = (id) => deleteDoc(doc(db, "skills", id));

export const getCertificates  = () => getAll("certificates");
export const addCertificate   = (d) => addDoc(col("certificates"), { ...d, createdAt: serverTimestamp() });
export const updateCertificate= (id, d) => updateDoc(doc(db, "certificates", id), d);
export const deleteCertificate= (id) => deleteDoc(doc(db, "certificates", id));

export const getServices      = () => getAll("services");
export const addService       = (d) => addDoc(col("services"), { ...d, createdAt: serverTimestamp() });
export const updateService    = (id, d) => updateDoc(doc(db, "services", id), d);
export const deleteService    = (id) => deleteDoc(doc(db, "services", id));

export const getTestimonials  = () => getAll("testimonials");
export const addTestimonial   = (d) => addDoc(col("testimonials"), { ...d, createdAt: serverTimestamp() });
export const updateTestimonial= (id, d) => updateDoc(doc(db, "testimonials", id), d);
export const deleteTestimonial= (id) => deleteDoc(doc(db, "testimonials", id));

// Contact form — saved to Firestore, email sent via EmailJS (configured in settings)
export const saveContactMessage = (d) =>
  addDoc(col("messages"), { ...d, createdAt: serverTimestamp(), read: false });

export const getMessages = () => getAll("messages", "createdAt");

export const getSettings  = async () => {
  try {
    const snap = await getDoc(doc(db, "settings", "main"));
    return snap.exists() ? snap.data() : null;
  } catch { return null; }
};
export const saveSettings = (data) => setDoc(doc(db, "settings", "main"), data, { merge: true });

export const updateOrder = async (collectionName, items) => {
  await Promise.all(items.map((item, i) =>
    updateDoc(doc(db, collectionName, item.id), { order: i + 1 })
  ));
};

export const seedData = async () => {
  const ts = serverTimestamp;

  const defaultSettings = {
    name: "Dursun İnel",
    title_tr: "Frontend Manager & Developer",
    title_en: "Frontend Manager & Developer",
    desc_tr: "2020'dan bu yana web geliştirme alanında aktif olarak çalışıyorum. HTML, CSS, JavaScript ve React ile kullanıcı deneyimini ön planda tutan projeler üretiyorum.",
    desc_en: "I have been actively working in web development since 2020. I build projects prioritizing user experience with HTML, CSS, JavaScript and React.",
    email: "inl.dursun@gmail.com",
    linkedin: "https://linkedin.com/in/dursun-inel",
    github: "https://github.com/dursuninel",
    instagram: "https://instagram.com/dursuninel",
    years_exp: "5+",
    companies: "3",
    available: true,
    available_text_tr: "Yeni projelere açığım",
    available_text_en: "Available for new projects",
    cta_title_tr: "Projenizi Hayata Geçirelim",
    cta_title_en: "Let's Build Your Project",
    cta_desc_tr: "Web siteniz için modern, hızlı ve profesyonel bir çözüm arıyorsanız doğru yerdesiniz. Ücretsiz keşif görüşmesi için formu doldurun.",
    cta_desc_en: "If you're looking for a modern, fast and professional solution for your website, you're in the right place. Fill out the form for a free discovery call.",
    process_steps_tr: ["Keşif Görüşmesi", "Tasarım & Planlama", "Geliştirme", "Teslim & Destek"],
    process_steps_en: ["Discovery Call", "Design & Planning", "Development", "Delivery & Support"],
    process_descs_tr: ["Projenizi, hedef kitlenizi ve beklentilerinizi detaylıca konuşuruz.", "Wireframe ve teknik mimari planını birlikte şekillendiririz.", "Hızlı, responsive ve SEO uyumlu kod yazarım. Sizi sürekli bilgilendiririm.", "Projeyi teslim eder, gerekli eğitimi veririm. Sonrasında da yanınızdayım."],
    process_descs_en: ["We talk through your project, target audience and expectations in detail.", "We shape the wireframe and technical architecture plan together.", "I write fast, responsive and SEO-friendly code. I keep you informed throughout.", "I deliver the project and provide necessary training. I'm here for you after too."],
    terminal_theme: "dark",
    terminal_title: "terminal — dursun@portfolio",
    terminal_username: "dursun",
    terminal_hostname: "portfolio",
    terminal_lines_tr: [
      { cmd: "whoami", out: "dursun_inel" },
      { cmd: "cat role.txt", out: "Frontend Manager & Developer" },
      { cmd: "ls skills/", out: "react/   javascript/   css/   node/   firebase/" },
      { cmd: "echo $EXPERIENCE", out: "5+ yıl, 17 canlı proje" },
    ],
    terminal_lines_en: [
      { cmd: "whoami", out: "dursun_inel" },
      { cmd: "cat role.txt", out: "Frontend Manager & Developer" },
      { cmd: "ls skills/", out: "react/   javascript/   css/   node/   firebase/" },
      { cmd: "echo $EXPERIENCE", out: "5+ years, 17 live projects" },
    ],
    terminal_typing_speed: 60,
    terminal_output_color: "#e8ff47",
    // EmailJS config (user fills these in admin settings)
    emailjs_service_id: "",
    emailjs_template_id: "",
    emailjs_public_key: "",
  };

  await setDoc(doc(db, "settings", "main"), defaultSettings);

  const projects = [
    { name_tr: "Falcon Savunma", name_en: "Falcon Savunma", url: "https://falconsavunma.com", tag: "React", order: 1 },
    { name_tr: "Bloom Design", name_en: "Bloom Design", url: "https://bloomco.com.tr", tag: "", order: 2 },
    { name_tr: "Safira Trade", name_en: "Safira Trade", url: "https://safiratrade.com.tr", tag: "", order: 3 },
    { name_tr: "İletişim İstanbul", name_en: "İletişim İstanbul", url: "https://iletisimistanbul.com.tr", tag: "", order: 4 },
    { name_tr: "Vatoz Robotik", name_en: "Vatoz Robotic", url: "https://vatozrobotic.com.tr", tag: "", order: 5 },
    { name_tr: "Mert Özcan Studio", name_en: "Mert Özcan Studio", url: "https://mertozcanstudio.com", tag: "", order: 6 },
    { name_tr: "Rigel Event", name_en: "Rigel Event", url: "https://rigelevent.com", tag: "", order: 7 },
    { name_tr: "Güneri Gürer", name_en: "Güneri Gürer", url: "https://gunerigurer.com", tag: "", order: 8 },
    { name_tr: "Acme Turizm", name_en: "Acme Tourism", url: "https://acmeturizm.com", tag: "", order: 9 },
    { name_tr: "On On Point", name_en: "On On Point", url: "https://ononpoint.com", tag: "", order: 10 },
    { name_tr: "Değer Kaybını Al", name_en: "Depreciation Claim", url: "https://degerkaybinial.com.tr", tag: "", order: 11 },
    { name_tr: "Canes Polimer", name_en: "Canes Polimer", url: "https://canespolimer.com", tag: "", order: 12 },
    { name_tr: "Kuzey Marmara OSGB", name_en: "Kuzey Marmara OSGB", url: "https://kuzeymarmaraosgb.com", tag: "", order: 13 },
    { name_tr: "Alsaç Araç Kiralama", name_en: "Alsaç Car Rental", url: "https://alsacarackiralama.com.tr", tag: "", order: 14 },
    { name_tr: "Dr. Mustafa Oral Oncül", name_en: "Dr. Mustafa Oral Oncül", url: "https://oraloncul.com.tr", tag: "", order: 15 },
    { name_tr: "Melyaz", name_en: "Melyaz", url: "https://melyaz.org", tag: "", order: 16 },
    { name_tr: "Say Danışmanlık", name_en: "Say Consulting", url: "https://sayyachting.com", tag: "", order: 17 },
  ];

  const experiences = [
    { role_tr: "Frontend Manager", role_en: "Frontend Manager", company: "Digimondi Dijital Ajans", date_tr: "2022 — 2025", date_en: "2022 — 2025", desc_tr: "Frontend ekibinin yöneticisi olarak koordinasyon ve yeni ekip üyelerinin eğitiminden sorumlu oldum.", desc_en: "As the head of the Frontend team, I was responsible for coordination and training of new team members.", order: 1 },
    { role_tr: "Frontend Developer", role_en: "Frontend Developer", company: "Digimondi Dijital Ajans", date_tr: "2022", date_en: "2022", desc_tr: "AcerPro'nun iştiraki Digimondi'ye geçiş yaparak müşteri iletişim becerilerimi geliştirdim.", desc_en: "I moved to Digimondi, a subsidiary of AcerPro, where I improved my customer communication skills.", order: 2 },
    { role_tr: "Frontend Developer", role_en: "Frontend Developer", company: "AcerPro Bilişim Teknolojileri A.Ş.", date_tr: "2020 — 2022", date_en: "2020 — 2022", desc_tr: "Sektöre ilk girişimde HTML, CSS ve JavaScript ile projeler ürettim.", desc_en: "With my first entry into the sector, I developed my skills with HTML, CSS, JS and made projects.", order: 3 },
  ];

  const skills = ["HTML5","CSS3","JavaScript","React.js","Bootstrap","jQuery","Node.js","Express.js","Rest API","Firebase","MySQL","MSSQL","PHP","C#","Responsive Design","Google Analytics"]
    .map((name, i) => ({ name, order: i + 1 }));

  const certificates = [
    { issuer: "FreeCodeCamp", name_tr: "Responsive Web Design", name_en: "Responsive Web Design", url: "", order: 1 },
    { issuer: "Udemy", name_tr: "Detaylı HTML ve CSS Eğitimi", name_en: "Detailed HTML & CSS Training", url: "", order: 2 },
    { issuer: "Udemy", name_tr: "Javascript Programlama Eğitimi", name_en: "Javascript Programming Training", url: "", order: 3 },
    { issuer: "Turkcell Geleceği Yazanlar", name_tr: "React 101 & React 201", name_en: "React 101 & React 201", url: "", order: 4 },
    { issuer: "Turkcell Geleceği Yazanlar", name_tr: "Web 101, 201, 301, 302", name_en: "Web 101, 201, 301, 302", url: "", order: 5 },
    { issuer: "Turkcell Geleceği Yazanlar", name_tr: "Database Querying with SQL 101", name_en: "Database Querying with SQL 101", url: "", order: 6 },
  ];

  const services = [
    { title_tr: "Kurumsal Web Sitesi", title_en: "Corporate Website", desc_tr: "Markanızı en iyi şekilde yansıtan, hızlı ve profesyonel web siteleri.", desc_en: "Fast and professional websites that best reflect your brand.", icon: "◈", order: 1 },
    { title_tr: "Landing Page", title_en: "Landing Page", desc_tr: "Dönüşüm odaklı, tek sayfalık tanıtım ve satış sayfaları.", desc_en: "Conversion-focused single-page promotional and sales pages.", icon: "◉", order: 2 },
    { title_tr: "React / SPA Uygulaması", title_en: "React / SPA Application", desc_tr: "Modern, dinamik ve ölçeklenebilir React tabanlı web uygulamaları.", desc_en: "Modern, dynamic and scalable React-based web applications.", icon: "◇", order: 3 },
    { title_tr: "Panel & Dashboard", title_en: "Admin Panel & Dashboard", desc_tr: "Verilerinizi yönetmek için özel tasarım yönetim panelleri.", desc_en: "Custom-designed admin panels to manage your data.", icon: "◎", order: 4 },
  ];

  for (const p of projects) await addDoc(col("projects"), { ...p, createdAt: ts() });
  for (const e of experiences) await addDoc(col("experiences"), { ...e, createdAt: ts() });
  for (const s of skills) await addDoc(col("skills"), { ...s, createdAt: ts() });
  for (const c of certificates) await addDoc(col("certificates"), { ...c, createdAt: ts() });
  for (const s of services) await addDoc(col("services"), { ...s, createdAt: ts() });
};
